import classNames from "classnames";
import { useCombobox } from "downshift";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useMember } from "../../context/memberContext";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import useToggle from "../../hooks/useToggle";
import { SP, useMembers } from "../../http/serviceportalApi";
import { useTranslations } from "../../hooks/useTranslations";
import SVGClose from "../../icons/Close.svg?react";
import SVGExpandLess from "../../icons/ExpandLess.svg?react";
import SVGExpandMore from "../../icons/ExpandMore.svg?react";
import SVGSearch from "../../icons/Search.svg?react";
import ErrorText from "../ui/ErrorText";
import HighlightText from "../ui/HighlightText";
import Icon from "../ui/Icon";
import LoadingSpinner from "../ui/LoadingSpinner";
import styles from "./MemberSelect.module.css";

interface Props {
  className?: string;
  theme?: "light" | "dark";
}

const MemberSelect = ({ className, theme = "light" }: Props) => {
  const { i18n } = useTranslations();
  const { member, hasOnlyOneMember, status, error, setMember } = useMember();
  const [pendingMember, setPendingMember] = useState<SP.Member | undefined>();

  useEffect(() => {
    if (member) setPendingMember(undefined);
  }, [member]);

  const menu = useToggle();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useOnClickOutside(searchRef, menu.close);

  useEffect(() => {
    if (!menu.isOpen) return;

    inputRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Tab") menu.close();
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menu.isOpen]);

  const memberName = member?.name ?? pendingMember?.name;
  const isValidating = status === "validating";
  const errorText = error
    ? i18n.memberSelect.loadError +
      (pendingMember?.name ? ` (${pendingMember.name})` : "")
    : undefined;

  const label = (
    <div className={styles.label}>
      {errorText ? (
        <ErrorText text={errorText} className={styles.error} />
      ) : (
        memberName && (
          <span
            className={classNames(styles.memberName, {
              [styles.memberNamePending]: isValidating,
            })}
            title={memberName}
          >
            {memberName}
          </span>
        )
      )}
    </div>
  );
  const loadingSpinner = <LoadingSpinner delayed={175} size="small" />;

  return (
    <div
      className={classNames(styles.memberSelect, className, {
        [styles.memberSelectDark]: theme === "dark",
        [styles.memberSelectOpen]: menu.isOpen,
        [styles.memberSelectOpenDark]: menu.isOpen && theme === "dark",
      })}
      ref={searchRef}
    >
      {hasOnlyOneMember ? (
        <div className={styles.labelAndIcon}>
          {label}
          {isValidating && loadingSpinner}
        </div>
      ) : (
        <button
          className={classNames(styles.button, styles.labelAndIcon, {
            [styles.buttonOpen]: menu.isOpen,
          })}
          onClick={menu.toggle}
          disabled={isValidating}
        >
          {label}
          {isValidating ? (
            loadingSpinner
          ) : (
            <Icon
              glyph={menu.isOpen ? SVGExpandLess : SVGExpandMore}
              className={styles.expandIcon}
            />
          )}
        </button>
      )}
      {menu.isOpen && (
        <MemberSelectMenu
          ref={inputRef}
          onSelectedItemChange={(m) => {
            menu.close();
            setPendingMember(m);
            setMember(m);
          }}
        />
      )}
    </div>
  );
};

interface MemberSelectMenuProps {
  onSelectedItemChange: (selectedItem: SP.Member) => void;
}

const MemberSelectMenu = forwardRef<HTMLInputElement, MemberSelectMenuProps>(
  ({ onSelectedItemChange }: MemberSelectMenuProps, ref) => {
    const { i18n } = useTranslations();
    const [inputValue, setInputValue] = useState("");

    const aborter = useRef(new AbortController());
    const { data, isValidating, error } = useMembers(
      { q: inputValue, page: 1, page_size: 10 },
      aborter.current.signal,
    );
    const isSuccess = !isValidating && !error;

    const items = data?.items ?? [];

    const { getMenuProps, getInputProps, highlightedIndex, getItemProps } =
      useCombobox({
        defaultIsOpen: true,
        items,
        itemToString: (item) => (item ? item.name : ""),
        inputValue,
        onStateChange: ({ inputValue, type, selectedItem }) => {
          switch (type) {
            case useCombobox.stateChangeTypes.InputChange:
              aborter.current.abort();
              aborter.current = new AbortController();
              setInputValue(inputValue ?? "");
              break;
            case useCombobox.stateChangeTypes.InputKeyDownEnter:
            case useCombobox.stateChangeTypes.ItemClick:
            case useCombobox.stateChangeTypes.InputBlur:
              if (!selectedItem) return;
              setInputValue(selectedItem.name);
              onSelectedItemChange?.(selectedItem);
              break;
            default:
              break;
          }
        },
      });

    return (
      <div className={styles.menu}>
        <hr className={styles.separator} />
        <div className={styles.search}>
          <div className={styles.combobox}>
            <input
              {...getInputProps({ ref })}
              type="search"
              placeholder={i18n.memberSelect.searchPlaceholder}
              spellCheck="false"
            />
          </div>
          {inputValue === "" && (
            <Icon glyph={SVGSearch} className={styles.searchIcon} />
          )}
          {inputValue !== "" && (
            <Icon
              glyph={SVGClose}
              className={styles.clearIcon}
              onClick={() => setInputValue("")}
            />
          )}
        </div>
        <span className={styles.title}>{i18n.memberSelect.groupTitle}</span>
        <dl {...getMenuProps()} className={styles.suggestions}>
          {isSuccess && items.length === 0 && (
            <p className={styles.emptyText}>
              <span>
                {inputValue !== ""
                  ? i18n.memberSelect.noResults(inputValue)
                  : i18n.memberSelect.empty}
              </span>
            </p>
          )}
          {isValidating && (
            <div className={styles.status}>
              <LoadingSpinner delayed={175} />
            </div>
          )}
          {error && (
            <div className={styles.status}>
              <ErrorText text={i18n.memberSelect.loadMembersError} />
            </div>
          )}
          {data?.items.map((item, index) => (
            <dd
              className={classNames(styles.suggestion, {
                [styles.highlightedSuggestion]: index === highlightedIndex,
              })}
              key={index}
              {...getItemProps?.({
                item,
                index,
              })}
            >
              <span className={styles.memberRow}>
                <HighlightText
                  className={styles.memberName}
                  text={item.name}
                  highlight={inputValue}
                  showTitle={true}
                />
                <HighlightText
                  className={styles.memberId}
                  text={item.id.toString()}
                  highlight={inputValue}
                />
              </span>
            </dd>
          ))}
        </dl>
      </div>
    );
  },
);

export default MemberSelect;
